import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Plus,
  ArrowLeft,
  Trash2,
  Pencil,
  Search,
  ChevronLeft,
  ChevronRight,
  ImagePlus,
  Tag,
  Save,
  AlertCircle,
  Loader2,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  save_update_blog_category,
  get_all_blog_category,
  delete_blog_category,
  toggle_blog_category,
} from "../ServiceConnection/serviceconnection.js";

// ─── Constants ────────────────────────────────────────────────────────────────
// Backend AdminBlogCategory model:
// primary_id, category_name, slug, short_description, image_url,
// is_active, sort_order, flag, entry_date

const ITEMS_PER_PAGE = 5;

const EMPTY_FORM = {
  primary_id: "0",
  category_name: "",
  slug: "",
  short_description: "",
  image_url: "",   // existing image URL (for edit)
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

// Auto-generate slug from category name
const toSlug = (str) =>
  str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

// ─── Delete Modal ─────────────────────────────────────────────────────────────
const DeleteModal = ({ category, onConfirm, onClose, deleteLoader }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div
      className="absolute inset-0 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    />
    <div className="relative z-10 w-full max-w-sm rounded-2xl border border-border bg-card shadow-2xl">
      <div className="p-6 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
          <Trash2 className="h-6 w-6 text-destructive" />
        </div>
        <h3 className="mb-2 text-base font-bold text-foreground">
          Delete Blog Category?
        </h3>
        <p className="text-sm text-muted-foreground">
          Are you sure you want to delete{" "}
          <span className="font-semibold text-foreground">
            "{category?.category_name}"
          </span>
          ? This cannot be undone.
        </p>
      </div>
      <div className="flex gap-3 border-t border-border p-4">
        <Button
          variant="outline"
          className="flex-1 rounded-xl"
          onClick={onClose}
          disabled={deleteLoader}
        >
          Cancel
        </Button>
        <Button
          disabled={deleteLoader}
          className="flex-1 gap-2 rounded-xl bg-destructive text-white hover:bg-destructive/90"
          onClick={onConfirm}
        >
          {deleteLoader ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
          Yes, Delete
        </Button>
      </div>
    </div>
  </div>
);

// ─── Image Upload Field ───────────────────────────────────────────────────────
const ImageUploadField = ({ id, name, label, hint, file, onChange, oldUrl }) => (
  <div>
    <label className="mb-1.5 block text-xs font-semibold text-foreground">
      {label}
    </label>
    <label
      htmlFor={id}
      className="flex h-10 w-full cursor-pointer items-center justify-between rounded-lg border border-input bg-background px-3 text-sm transition-colors hover:bg-accent"
    >
      <span className={file ? "text-foreground" : "text-muted-foreground"}>
        {file
          ? file.name
          : oldUrl
            ? "Image uploaded — click to replace"
            : `Choose ${label}`}
      </span>
      <ImagePlus className="h-4 w-4 shrink-0 text-muted-foreground" />
    </label>
    <input
      type="file"
      id={id}
      name={name}
      onChange={onChange}
      className="hidden"
      accept="image/*"
    />
    {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    {oldUrl && !file && (
      <div className="mt-2 h-16 w-24 overflow-hidden rounded-lg border border-border">
        <img src={oldUrl} alt="current" className="h-full w-full object-cover" />
      </div>
    )}
  </div>
);

// ─── Page ─────────────────────────────────────────────────────────────────────
const BlogCategories = () => {
  const { toast } = useToast();

  const [categories, setCategories] = useState([]);
  const [formVisible, setFormVisible] = useState(false);
  const [fetchLoader, setFetchLoader] = useState(false);
  const [saveLoader, setSaveLoader] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [toggleLoader, setToggleLoader] = useState(null); // stores primary_id being toggled
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedFile, setSelectedFile] = useState(null); // category_image
  const [errors, setErrors] = useState({});

  // ─── Fetch all categories ────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      setFetchLoader(true);
      const fd = new FormData();
      fd.append("flag", "1");
      fd.append("call_id", "");

      const response = await server_post_data(get_all_blog_category, fd);

      if (!response.data.error) {
        setCategories(response.data.message.data_category || []);
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setFetchLoader(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Field helper ────────────────────────────────────────────────────────
  const setField = (key, val) => {
    setForm((prev) => ({ ...prev, [key]: val }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: null }));
  };

  // ─── Reset form ───────────────────────────────────────────────────────────
  const resetForm = () => {
    setForm(EMPTY_FORM);
    setSelectedFile(null);
    setErrors({});
  };

  // ─── Save / Update ────────────────────────────────────────────────────────
  const handleSave = async () => {
    const e = {};
    if (!form.category_name.trim()) e.category_name = "Category name is required";
    if (!form.slug.trim()) e.slug = "Slug is required";
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    try {
      setSaveLoader(true);

      const fd = new FormData();
      fd.append("main_id", form.primary_id);
      fd.append("admin_id", "1"); // pass logged-in admin id as needed
      fd.append("category_name", form.category_name.trim());
      fd.append("slug", form.slug.trim().toLowerCase());
      fd.append("short_description", form.short_description || "");
      fd.append("old_image", form.image_url || "");
      if (selectedFile) fd.append("category_image", selectedFile);

      const response = await server_post_data(save_update_blog_category, fd);

      if (!response.data.error) {
        toast({ title: "Success", description: response.data.message });
        resetForm();
        setFormVisible(false);
        fetchData();
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaveLoader(false);
    }
  };

  // ─── Delete ───────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteLoader(true);
      const fd = new FormData();
      fd.append("main_id", deleteTarget.primary_id);

      const response = await server_post_data(delete_blog_category, fd);

      if (!response.data.error) {
        toast({ title: "Deleted", description: response.data.message });
        setDeleteTarget(null);
        fetchData();
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeleteLoader(false);
    }
  };

  // ─── Toggle active status ─────────────────────────────────────────────────
  const handleToggle = async (category) => {
    try {
      setToggleLoader(category.primary_id);
      const fd = new FormData();
      fd.append("main_id", category.primary_id);
      fd.append("is_active", !category.is_active);

      const response = await server_post_data(toggle_blog_category, fd);

      if (!response.data.error) {
        toast({ title: "Updated", description: response.data.message });
        fetchData();
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setToggleLoader(null);
    }
  };

  // ─── Edit ────────────────────────────────────────────────────────────────
  const handleEdit = async (category) => {
    try {
      setFetchLoader(true);
      const fd = new FormData();
      fd.append("flag", "3");
      fd.append("call_id", category.primary_id);

      const response = await server_post_data(get_all_blog_category, fd);

      if (!response.data.error) {
        const data = response.data.message.data_category?.[0] || category;
        setForm({
          primary_id: data.primary_id,
          category_name: data.category_name || "",
          slug: data.slug || "",
          short_description: data.short_description || "",
          image_url: data.image_url || "",
        });
      } else {
        // fallback to local data
        setForm({
          primary_id: category.primary_id,
          category_name: category.category_name || "",
          slug: category.slug || "",
          short_description: category.short_description || "",
          image_url: category.image_url || "",
        });
      }
    } catch (error) {
      console.error(error);
      setForm({
        primary_id: category.primary_id,
        category_name: category.category_name || "",
        slug: category.slug || "",
        short_description: category.short_description || "",
        image_url: category.image_url || "",
      });
    } finally {
      setFetchLoader(false);
    }

    setSelectedFile(null);
    setErrors({});
    setFormVisible(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ─── Filter + Pagination ──────────────────────────────────────────────────
  const filtered = categories.filter((c) =>
    [c.category_name, c.slug, c.short_description]
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  const paginated = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  // ─────────────────────────────────────────────────────────────────────────
  // LIST VIEW
  // ─────────────────────────────────────────────────────────────────────────
  if (!formVisible) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-bold text-foreground">Blog Categories</h2>
            <p className="text-sm text-muted-foreground">
              Manage categories for your blog posts.
            </p>
          </div>
          <Button
            className="gap-2 self-start rounded-xl"
            onClick={() => {
              resetForm();
              setFormVisible(true);
            }}
          >
            <Plus className="h-4 w-4" /> Add Category
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search categories..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="pl-9"
          />
        </div>

        {/* Table */}
        <Card>
          {/* Loading */}
          {fetchLoader && (
            <CardContent className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </CardContent>
          )}

          {/* Empty */}
          {!fetchLoader && paginated.length === 0 && (
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
                <Tag className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-sm font-semibold text-foreground">
                No categories found
              </p>
              <p className="mb-4 text-xs text-muted-foreground">
                {search
                  ? "Try a different search term."
                  : "Add your first blog category to get started."}
              </p>
              {!search && (
                <Button
                  size="sm"
                  className="gap-2 rounded-xl"
                  onClick={() => {
                    resetForm();
                    setFormVisible(true);
                  }}
                >
                  <Plus className="h-4 w-4" /> Add Category
                </Button>
              )}
            </CardContent>
          )}

          {/* Rows */}
          {!fetchLoader && paginated.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Date
                      </th>
                      <th className="hidden px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground sm:table-cell">
                        Image
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Category
                      </th>
                      <th className="hidden px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground md:table-cell">
                        Slug
                      </th>
                      <th className="hidden px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground lg:table-cell">
                        Description
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((cat, index) => (
                      <tr
                        key={cat.primary_id}
                        className="border-b border-border transition-colors hover:bg-muted/30"
                      >
                        <td className="px-6 py-4 text-sm text-muted-foreground">
                          {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                        </td>
                        <td className="px-6 py-4">
                          <p className="whitespace-nowrap text-xs text-muted-foreground">
                            {formatDate(cat.entry_date)}
                          </p>
                        </td>
                        <td className="hidden px-6 py-4 sm:table-cell">
                          <div className="h-12 w-16 overflow-hidden rounded-lg border border-border bg-muted">
                            {cat.image_url ? (
                              <img
                                src={cat.image_url}
                                alt={cat.category_name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full items-center justify-center">
                                <ImagePlus className="h-4 w-4 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="max-w-[160px] px-6 py-4">
                          <p className="truncate text-sm font-semibold text-foreground">
                            {cat.category_name}
                          </p>
                        </td>
                        <td className="hidden max-w-[160px] px-6 py-4 md:table-cell">
                          <p className="truncate text-xs text-muted-foreground font-mono">
                            {cat.slug}
                          </p>
                        </td>
                        <td className="hidden max-w-[200px] px-6 py-4 lg:table-cell">
                          <p className="truncate text-xs text-muted-foreground">
                            {cat.short_description || "—"}
                          </p>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={() => handleToggle(cat)}
                            disabled={toggleLoader === cat.primary_id}
                            title={cat.is_active ? "Active — click to deactivate" : "Inactive — click to activate"}
                            className="inline-flex items-center justify-center"
                          >
                            {toggleLoader === cat.primary_id ? (
                              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            ) : cat.is_active ? (
                              <ToggleRight className="h-6 w-6 text-green-500" />
                            ) : (
                              <ToggleLeft className="h-6 w-6 text-muted-foreground" />
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              title="Edit"
                              onClick={() => handleEdit(cat)}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              title="Delete"
                              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => setDeleteTarget(cat)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between border-t border-border px-6 py-4">
                <p className="text-xs text-muted-foreground">
                  Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{" "}
                  {Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} of{" "}
                  {filtered.length} categories
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon-sm"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(
                      (p) =>
                        p === 1 ||
                        p === totalPages ||
                        Math.abs(p - currentPage) <= 1,
                    )
                    .map((p, i, arr) => (
                      <span key={p} className="flex items-center gap-1">
                        {i > 0 && arr[i - 1] !== p - 1 && (
                          <span className="px-1 text-xs text-muted-foreground">
                            …
                          </span>
                        )}
                        <button
                          onClick={() => setCurrentPage(p)}
                          className={`flex h-7 w-7 items-center justify-center rounded-lg text-xs font-medium transition-all ${
                            currentPage === p
                              ? "bg-gradient-primary text-white shadow-glow"
                              : "border border-border text-muted-foreground hover:border-primary/30 hover:text-primary"
                          }`}
                        >
                          {p}
                        </button>
                      </span>
                    ))}
                  <Button
                    variant="outline"
                    size="icon-sm"
                    disabled={currentPage === totalPages || totalPages === 0}
                    onClick={() => setCurrentPage((p) => p + 1)}
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>

        {/* Delete Modal */}
        {deleteTarget && (
          <DeleteModal
            category={deleteTarget}
            onConfirm={handleDeleteConfirm}
            onClose={() => setDeleteTarget(null)}
            deleteLoader={deleteLoader}
          />
        )}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // FORM VIEW
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">
            {form.primary_id !== "0" ? "Edit Blog Category" : "Add New Category"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {form.primary_id !== "0"
              ? "Update the category details below."
              : "Fill in the details to create a new blog category."}
          </p>
        </div>
        <Button
          variant="outline"
          className="gap-2 rounded-xl"
          onClick={() => {
            resetForm();
            setFormVisible(false);
          }}
        >
          <ArrowLeft className="h-4 w-4" /> Back to List
        </Button>
      </div>

      {/* Error banner */}
      {Object.values(errors).some(Boolean) && (
        <div className="flex items-start gap-3 rounded-2xl border border-destructive/20 bg-destructive/5 p-4">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <p className="text-sm font-semibold text-destructive">
            Please fix the errors below before saving.
          </p>
        </div>
      )}

      {/* Category Image */}
      <Card>
        <div className="border-b border-border px-6 py-4">
          <h3 className="text-sm font-bold text-foreground">Category Image</h3>
          <p className="text-xs text-muted-foreground">
            Upload a thumbnail image for this category
          </p>
        </div>
        <CardContent className="p-6">
          <ImageUploadField
            id="category_image"
            name="category_image"
            label="Category Image"
            hint="Recommended size: 400 × 250 px"
            file={selectedFile}
            onChange={(e) => setSelectedFile(e.target.files[0] || null)}
            oldUrl={form.image_url}
          />
        </CardContent>
      </Card>

      {/* Category Details */}
      <Card>
        <div className="border-b border-border px-6 py-4">
          <h3 className="text-sm font-bold text-foreground">Category Details</h3>
          <p className="text-xs text-muted-foreground">
            Name, URL slug, and description
          </p>
        </div>
        <CardContent className="space-y-4 p-6">
          {/* Category Name */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-foreground">
              Category Name <span className="text-destructive">*</span>
            </label>
            <Input
              placeholder="e.g. Health & Wellness"
              value={form.category_name}
              onChange={(e) => {
                const val = e.target.value;
                setField("category_name", val);
                // Auto-fill slug only when creating new
                if (form.primary_id === "0") {
                  setForm((prev) => ({
                    ...prev,
                    category_name: val,
                    slug: toSlug(val),
                  }));
                  if (errors.category_name || errors.slug)
                    setErrors((prev) => ({
                      ...prev,
                      category_name: null,
                      slug: null,
                    }));
                }
              }}
              className={errors.category_name ? "border-destructive" : ""}
            />
            {errors.category_name && (
              <p className="mt-1 text-xs text-destructive">
                {errors.category_name}
              </p>
            )}
          </div>

          {/* Slug */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-foreground">
              URL Slug <span className="text-destructive">*</span>
            </label>
            <Input
              placeholder="e.g. health-wellness"
              value={form.slug}
              onChange={(e) =>
                setField("slug", e.target.value.toLowerCase().replace(/\s+/g, "-"))
              }
              className={errors.slug ? "border-destructive" : ""}
            />
            {errors.slug && (
              <p className="mt-1 text-xs text-destructive">{errors.slug}</p>
            )}
            <p className="mt-1 text-[11px] text-muted-foreground">
              Auto-filled from name. Must be unique.
            </p>
          </div>

          {/* Short Description */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-foreground">
              Short Description
            </label>
            <textarea
              rows={3}
              placeholder="Enter a short description for this category..."
              value={form.short_description}
              onChange={(e) => setField("short_description", e.target.value)}
              className="flex min-h-[80px] w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
        </CardContent>
      </Card>

      {/* Save / Cancel */}
      <div className="flex justify-end gap-3 border-t border-border pt-4">
        <Button
          variant="outline"
          className="rounded-xl"
          onClick={() => {
            resetForm();
            setFormVisible(false);
          }}
        >
          Cancel
        </Button>
        <Button
          className="gap-2 rounded-xl"
          disabled={saveLoader}
          onClick={handleSave}
        >
          {saveLoader ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              {form.primary_id !== "0" ? "Save Changes" : "Create Category"}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default BlogCategories;